import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import { useCreateGlobalMeeting, useUpdateGlobalMeeting, type GlobalMeeting } from '../../api/hooks/useGlobalMeetings';
import { useNotifications } from '../../context/NotificationContext';
import { ApiError } from '../../api/client';
import { AppDialogTitle } from '../../components/common/AppDialogTitle';

interface GlobalMeetingDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: GlobalMeeting | null;
  onClose: () => void;
}

interface FormState {
  titel: string;
  datum: Dayjs | null;
  uhrzeit: Dayjs | null;
  beschreibung: string;
  teilnehmer: string;
  ort: string;
  link_uri: string;
}

const EMPTY: FormState = {
  titel: '',
  datum: null,
  uhrzeit: null,
  beschreibung: '',
  teilnehmer: '',
  ort: '',
  link_uri: '',
};

/** Anlage-/Bearbeiten-Dialog fuer ein globales (tenant-weites) Meeting. */
export function GlobalMeetingDialog({ open, mode, initial, onClose }: GlobalMeetingDialogProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const createMutation = useCreateGlobalMeeting();
  const updateMutation = useUpdateGlobalMeeting();
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setForm({
        titel: initial.titel ?? '',
        datum: initial.datum ? dayjs(initial.datum) : null,
        uhrzeit: initial.uhrzeit ? dayjs(`2000-01-01T${initial.uhrzeit}`) : null,
        beschreibung: initial.beschreibung ?? '',
        teilnehmer: initial.teilnehmer ?? '',
        ort: initial.ort ?? '',
        link_uri: initial.link_uri ?? '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, mode, initial]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    if (!form.titel.trim()) {
      notify(t('globalMeeting.titleRequired', 'Titel ist ein Pflichtfeld'), 'warning');
      return;
    }
    if (!form.datum) {
      notify(t('globalMeeting.dateRequired', 'Datum ist ein Pflichtfeld'), 'warning');
      return;
    }
    const payload: Record<string, unknown> = {
      titel: form.titel.trim(),
      datum: form.datum.format('YYYY-MM-DD'),
      uhrzeit: form.uhrzeit ? form.uhrzeit.format('HH:mm') : null,
      beschreibung: form.beschreibung.trim() || null,
      teilnehmer: form.teilnehmer.trim() || null,
      ort: form.ort.trim() || null,
      link_uri: form.link_uri.trim() || null,
    };
    try {
      if (mode === 'edit' && initial) {
        await updateMutation.mutateAsync({ id: initial.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      notify(tc('status.saved', 'Gespeichert'), 'success');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : tc('errors.generic', 'Fehler');
      notify(msg, 'error');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="sm" fullWidth>
        <AppDialogTitle>
          {mode === 'edit'
            ? t('globalMeeting.editTitle', 'Globales Meeting bearbeiten')
            : t('globalMeeting.createTitle', 'Neues globales Meeting')}
        </AppDialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label={t('globalMeeting.titel', 'Titel')}
              value={form.titel}
              onChange={(e) => set('titel', e.target.value)}
            />
            <Stack direction="row" spacing={2}>
              <DatePicker
                label={t('globalMeeting.datum', 'Datum')}
                value={form.datum}
                onChange={(v) => set('datum', v)}
                slotProps={{ textField: { fullWidth: true, required: true, error: !form.datum } }}
              />
              <TimePicker
                label={t('globalMeeting.uhrzeit', 'Uhrzeit')}
                value={form.uhrzeit}
                onChange={(v) => set('uhrzeit', v)}
                ampm={false}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('globalMeeting.beschreibung', 'Beschreibung')}
              value={form.beschreibung}
              onChange={(e) => set('beschreibung', e.target.value)}
            />
            <TextField
              fullWidth
              label={t('globalMeeting.teilnehmer', 'Teilnehmer')}
              value={form.teilnehmer}
              onChange={(e) => set('teilnehmer', e.target.value)}
              placeholder={t('globalMeeting.teilnehmerPlaceholder', 'z. B. Projektleitungen, Lenkungskreis')}
            />
            <TextField
              fullWidth
              label={t('globalMeeting.ort', 'Ort')}
              value={form.ort}
              onChange={(e) => set('ort', e.target.value)}
            />
            <TextField
              fullWidth
              label={t('globalMeeting.linkUri', 'Link')}
              value={form.link_uri}
              onChange={(e) => set('link_uri', e.target.value)}
              placeholder="https://..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            {tc('actions.cancel', 'Abbrechen')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !form.titel.trim() || !form.datum}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {tc('actions.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
